import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Link, Stack, Typography, Paper, Tabs, Tab,
} from "@mui/material";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { DataGrid, gridFilteredSortedRowIdsSelector, useGridApiRef } from "@mui/x-data-grid";

export default function Step4Review({
  currentParent,
  history,
  onBreadcrumbClick,
  error,
  headerRows,
  rankStats,
  loading,
  reviewCandidates,
  dataGridColumns,
  dataGridStyles,
  onSelectHeaders,
  onClearSelection,
  selectionModel,
}) {
  const apiRef = useGridApiRef();
  const [statusTab, setStatusTab] = useState('all');

  const statusCounts = useMemo(() => {
    const counts = { NEW: 0, MERGED: 0, DB_ONLY: 0 };
    headerRows.forEach((row) => {
      const status = row?.match_status || 'UNKNOWN';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [headerRows]);

  const statusTabs = useMemo(() => ([
    { value: 'all', label: `ALL (${headerRows.length})` },
    { value: 'NEW', label: `NEW (${statusCounts.NEW || 0})` },
    { value: 'MERGED', label: `MERGED (${statusCounts.MERGED || 0})` },
    { value: 'DB_ONLY', label: `DB_ONLY (${statusCounts.DB_ONLY || 0})` },
  ]), [headerRows.length, statusCounts]);

  const statusFilteredRows = useMemo(() => {
    if (statusTab === 'all') return reviewCandidates;
    return reviewCandidates.filter((row) => row?.type !== 'HEADER' || row?.match_status === statusTab);
  }, [reviewCandidates, statusTab]);

  const statusHeaderCount = useMemo(() => (
    statusFilteredRows.filter((row) => row?.type === 'HEADER').length
  ), [statusFilteredRows]);

  useEffect(() => {
    if (!apiRef.current?.state) return;
    const currentPinned = apiRef.current.state.pinnedColumns || {};
    const left = currentPinned.left || [];
    if (left.includes('selection')) return;
    const nextLeft = ['selection', ...left.filter((field) => field !== 'selection')];
    const nextRight = currentPinned.right || [];
    apiRef.current.setState((state) => ({
      ...state,
      pinnedColumns: {
        left: nextLeft,
        right: nextRight,
      },
    }));
    apiRef.current.updateRenderContext?.();
  }, [apiRef, reviewCandidates.length]);

  const handleSelectAllVisible = () => {
    let candidateIds = [];
    if (apiRef.current) {
      const filteredIds = gridFilteredSortedRowIdsSelector(apiRef) || [];
      candidateIds = filteredIds
        .map((id) => apiRef.current.getRow(id))
        .filter((row) => row && row.type === 'HEADER')
        .map((row) => row.selection_id ?? row.id)
        .filter(Boolean);
    } else {
      candidateIds = statusFilteredRows
        .filter((row) => row?.type === 'HEADER')
        .map((row) => row.selection_id ?? row.id)
        .filter(Boolean);
    }
    if (candidateIds.length === 0) return;
    const nextSelection = Array.from(new Set([...selectionModel, ...candidateIds]));
    onSelectHeaders?.(nextSelection);
  };

  if (!currentParent) return null;

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <Typography variant="h6">Step 4: 結果のレビューと登録</Typography>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
        {history.map((node, index) => (
          <Link component="button" variant="body2" key={node.db_id} onClick={() => onBreadcrumbClick?.(node, index)}>
            {node.name}
          </Link>
        ))}
      </Breadcrumbs>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={statusTab}
          onChange={(event, value) => setStatusTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {statusTabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip label={`合計 ${headerRows.length}`} />
        {Object.entries(rankStats).map(([rank, count]) => (
          <Chip key={rank} label={`${rank}: ${count}`} size="small" />
        ))}
        <Box flexGrow={1} />
        <Button size="small" variant="outlined" onClick={handleSelectAllVisible} disabled={loading || statusHeaderCount === 0}>全選択</Button>
        <Button size="small" variant="text" onClick={onClearSelection} disabled={selectionModel.length === 0}>選択解除</Button>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, width: '100%', mt: 1 }}>
        {loading ? <CircularProgress /> : (
          <Paper variant="outlined" sx={{ height: '100%', p: 0.5 }}>
            <DataGrid
              density="compact"
              rows={statusFilteredRows}
              columns={dataGridColumns}
              getRowId={(r) => r.id}
              apiRef={apiRef}
              getRowHeight={() => 'auto'}
              hideFooter
              sortingOrder={['asc', 'desc', null]}
              sx={{
                minWidth: '100%',
                ...dataGridStyles,
                '& .MuiDataGrid-row': { backgroundColor: '#2f3437' },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#2f3437' },
                '& .group-header-row': {
                  backgroundColor: '#34393d',
                  borderTop: '2px solid #555',
                },
              }}
            />
          </Paper>
        )}
      </Box>
    </Stack>
  );
}
